/**
 * **The pane's head, and it never remounts.** KANON `ankiety-klienta-2026-09-16`.
 *
 * A title, a line saying which occurrence — or which two — the pane is about, and the one or two
 * controls that act on it.
 *
 * ── Why it is not inside `SurveyReport` and `SurveyCompare` any more ─────────────────────────
 * The pane's CONTENT is keyed on its subject so Motion can cross-fade a swap, which means the whole
 * subtree is unmounted and rebuilt every time the trainer presses a row. With the head inside it,
 * the control they had just pressed was destroyed under their finger: „zamień strony" took the
 * focus ring to `<body>` on every press, and so did the ✕ and the tick. The head stands beside that
 * box now and reconciles in place — the same `<button>` element, with a different name on it.
 *
 * That is also why the two modes are drawn by ONE component rather than by a head in each of the
 * two bodies: React keeps an element only while it stays the same type in the same position, and
 * two components swapping would have unmounted the row again.
 *
 * ── The head no longer carries `aria-live` ───────────────────────────────────────────────────
 * It used to announce itself, which made a region that is rebuilt on every press responsible for
 * saying what changed — the one shape a live region cannot have. The tab owns a single always-
 * mounted region instead, and this head is silent.
 */

import { useTranslation } from 'react-i18next';

import icon from '~/shared/ui/iconButton.module.css';

import {
  CloseIcon,
  MarkFailedAlert,
  MarkReviewedButton,
  SwapIcon,
  type SurveyColumnView,
} from '~/features/survey-report';

import type { SurveyPaneView } from '../model/surveyPane';
import styles from './SurveyPane.module.css';

export interface SurveyPaneHeadProps {
  /** The pane's hand-over class, from the tab's gate. This head is what replaces the bars. */
  readonly className?: string;
  readonly head: SurveyPaneView;
  /** The occurrence the report is of, for the one act this head carries. `null` in a comparison. */
  readonly column: SurveyColumnView | null;
  readonly onMarkReviewed: (columnId: number) => void;
  readonly marking: boolean;
  /** The last mark did not reach the backend. Said here, beside the control that tried. */
  readonly markFailed: boolean;
  /** „Zamień strony". The caller reverses the pair it holds — see `SurveyCompare`. */
  readonly onSwap: () => void;
  /** „Zamknij porównanie". Leaves compare mode and returns the pane to a report. */
  readonly onClose: () => void;
}

export function SurveyPaneHead({
  className,
  head,
  column,
  onMarkReviewed,
  marking,
  markFailed,
  onSwap,
  onClose,
}: SurveyPaneHeadProps) {
  const { t } = useTranslation();
  const comparing = head.mode === 'compare';

  return (
    <div className={`${String(styles.head)} ${className ?? ''}`}>
      <div className={styles.headText}>
        <h2 className={styles.title}>{head.title}</h2>
        <p className={styles.subtitle}>{head.subtitle}</p>
      </div>

      <MarkFailedAlert failed={markFailed} message={t('web:clientDetail.surveys.markFailed')} />

      <div className={styles.actions}>
        {comparing ? (
          <>
            <button
              type="button"
              className={`${String(icon.iconButton)} ${String(styles.action)}`}
              aria-label={t('web:clientDetail.surveys.swapSides')}
              title={t('web:clientDetail.surveys.swapSides')}
              onClick={onSwap}
            >
              <SwapIcon />
            </button>
            <button
              type="button"
              className={`${String(icon.iconButton)} ${String(styles.action)} ${String(styles.closeAction)}`}
              aria-label={t('web:clientDetail.surveys.closeCompare')}
              title={t('web:clientDetail.surveys.closeCompare')}
              onClick={onClose}
            >
              <CloseIcon />
            </button>
          </>
        ) : /* Write-once: the control goes once `reviewedAt` is set, and no „odznacz" exists. */
        column === null || column.reviewed ? null : (
          <MarkReviewedButton
            className={String(styles.markAction)}
            label={t('web:clientDetail.surveys.reviewed')}
            description={t('web:clientDetail.surveys.markReviewed', { at: column.ordinalLabel })}
            marking={marking}
            onMark={() => {
              onMarkReviewed(column.id);
            }}
          />
        )}
      </div>
    </div>
  );
}
